import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { BulkSelectEmployeeDialogComponent, SearchComponent } from '@app/components';
import { Columns as Column, Row } from '@app/components/simple-table/simple-table.component';
import { Reports, ReportsMap, Selections } from '@app/interfaces/participants-list.interface';
import { Employee } from '@app/models/employee/employee.model';
import { TranslatableKey } from '@app/types/translatable.type';
import { BaseForm } from '@forms/base.form';
import { TranslateService } from '@ngx-translate/core';
import { omitBy } from 'lodash-es';

@Component({
    selector: 'app-review-participants-table',
    templateUrl: './review-participants-table.template.html',
    styleUrls: ['./review-participants-table.style.scss'],
})
export class ReviewParticipantsTable extends BaseForm {
    @ViewChild('selectParticipants', { static: true }) selectParticipants: BulkSelectEmployeeDialogComponent;
    @ViewChild('searchBar', { static: true }) searchBar: SearchComponent;
    @Output() participantList = new EventEmitter<Map<number, ReportsMap>>();
    @Output() appSimpleTableRows = new EventEmitter<Row[]>();
    @Output() editparticipantName = new EventEmitter<string>();
    @Input() title: TranslatableKey = '';
    @Input() showInlineButton: boolean = false;

    @Input() set selections(selectionsvalue: Selections) {
        const differences = omitBy(this._selections, (value, key) => {
            return value === selectionsvalue[key as keyof Selections];
        });

        this._selections = JSON.parse(JSON.stringify(selectionsvalue));
        this.checkboxChanged(differences);
    }

    get selections(): Selections {
        return this._selections;
    }

    private _selections: Selections = { self: false, peers: false, manager: false, directReports: false };

    @Input() set reviewees(reviewees: Employee[]) {
        if (!reviewees?.length) {
            return;
        }
        this._reviewees = reviewees;
        this.setReviewees();
    }

    get reviewees(): Employee[] {
        return this._reviewees;
    }

    private _reviewees: Employee[] = [];

    revieweesParticipantsList: Map<number, ReportsMap> = new Map<number, ReportsMap>();
    isLoading: boolean = false;
    revieweeName: string = '';
    revieweesRelationships: Map<number, Reports> = new Map<number, Reports>();
    rows: Row[] = [];
    computedRows: Row[] = [];
    columns: Column[] = [
        { columnName: 'performance.participants.reviewee', columnSpan: 'columns-5' },
        { columnName: 'performance.participants.participants', columnSpan: 'columns-6' },
    ];

    constructor(private translateService: TranslateService) {
        super();
    }

    ngOnInit() {
        this.setReviewees();
    }

    private setReviewees(): void {
        this.isLoading = true;

        this.revieweesParticipantsList = new Map<number, ReportsMap>();
        this.rows = this.reviewees.map((reviewee: Employee) => {
            const participants: Reports = {
                peers: reviewee.activePeers,
                directReports: reviewee.activeDirectReports,
                self: [reviewee],
            };

            if (reviewee.manager) {
                participants.manager = [reviewee.manager];
            }
            //Reset Participant list upon Reviewee's list update
            this.revieweesRelationships.set(reviewee.id, participants);

            this.revieweesParticipantsList.set(reviewee.id, {
                manager: new Map<number, Employee>(),
                peers: new Map<number, Employee>(),
                directReports: new Map<number, Employee>(),
                self: new Map<number, Employee>(),
                others: new Map<number, Employee>(),
            });

            return this.mapEmployeesToRow(reviewee);
        });

        this.isLoading = false;
    }

    onSearch(searchQuery: string | null): void {
        if (searchQuery === null) {
            this.rows = this.computedRows;
            return;
        }

        const nameIncludesSearchQuery = (row: Row): boolean =>
            (row.cells[0] as { employee: Employee; isAvatar: boolean }).employee.fullName
                .toLowerCase()
                .includes(searchQuery.toLocaleLowerCase());

        this.rows = this.computedRows.filter(nameIncludesSearchQuery);
    }

    private mapEmployeesToRow(employee: Employee): Row {
        return {
            cells: [{ employee, isAvatar: true }, this.getAggregatedParticipantString(employee)],
        };
    }

    private getAggregatedParticipantString(reviewee: Employee): string {
        const selfCount = this.revieweesParticipantsList.get(reviewee.id)?.self?.size;
        const managerCount = this.revieweesParticipantsList.get(reviewee.id)?.manager?.size;
        const directReportsCount = this.revieweesParticipantsList.get(reviewee.id)?.directReports?.size;
        const peersCount = this.revieweesParticipantsList.get(reviewee.id)?.peers?.size;
        const othersCount = this.revieweesParticipantsList.get(reviewee.id)?.others?.size;

        let finalString = '';
        if (selfCount) {
            finalString += this.translateService.instant('performance.participants.self', { count: selfCount });
        }

        if (managerCount) {
            finalString += this.translateService.instant('performance.participants.manager', { count: managerCount });
        }

        if (directReportsCount) {
            finalString += this.translateService.instant('performance.participants.directReports', {
                count: directReportsCount,
            });
        }

        if (peersCount) {
            finalString += this.translateService.instant('performance.participants.peers', { count: peersCount });
        }

        if (othersCount) {
            finalString += this.translateService.instant('performance.participants.otherEmployees', {
                count: othersCount,
            });
        }

        //Final string to display the participants count per reviewee
        return finalString.substring(0, finalString.length - 2);
    }

    showParticipantsSelectDialog(index: number): void {
        this.revieweeName = this.reviewees[index].fullName;
        const revieweeId: number = this.reviewees[index].id;
        const groupedRevieweeParticipants = this.revieweesParticipantsList.get(revieweeId) ?? {
            manager: new Map<number, Employee>(),
            peers: new Map<number, Employee>(),
            directReports: new Map<number, Employee>(),
            self: new Map<number, Employee>(),
            others: new Map<number, Employee>(),
        };

        const participantsList: Employee[] = [
            ...groupedRevieweeParticipants.manager.values(),
            ...groupedRevieweeParticipants.self.values(),
            ...groupedRevieweeParticipants.directReports.values(),
            ...groupedRevieweeParticipants.peers.values(),
            ...groupedRevieweeParticipants.others.values(),
        ];

        this.selectParticipants.show<Employee[]>(participantsList).then((selected: Employee[]) => {
            if (selected) {
                const participantsRemoved = participantsList.filter((participant) => !selected?.includes(participant));

                const participantsAdded = selected.filter(
                    (selectedParticipant) => !participantsList?.includes(selectedParticipant)
                );

                this.updateRevieweesParticipantsList(participantsRemoved, participantsAdded, revieweeId);
            }
        });

        this.editparticipantName.emit(this.revieweeName);
    }

    private updateRevieweesParticipantsList(
        participantsRemoved: Employee[],
        participantsAdded: Employee[],
        revieweeId: number
    ): void {
        const revieweesRelationships = this.revieweesRelationships.get(revieweeId);

        //Update Reviewees Participants List
        participantsAdded.forEach((participant) => {
            if (revieweesRelationships?.manager && participant.is(revieweesRelationships?.manager[0])) {
                this.revieweesParticipantsList.get(revieweeId)?.manager?.set(participant.id, participant);
                return;
            }

            if (revieweesRelationships?.self && participant.is(revieweesRelationships?.self[0])) {
                this.revieweesParticipantsList.get(revieweeId)?.self?.set(participant.id, participant);
                return;
            }

            if (revieweesRelationships?.peers?.filter((peer) => peer.id == participant.id).length != 0) {
                this.revieweesParticipantsList.get(revieweeId)?.peers?.set(participant.id, participant);
                return;
            }

            if (
                revieweesRelationships?.directReports?.filter((directReport) => directReport.id == participant.id)
                    .length != 0
            ) {
                this.revieweesParticipantsList.get(revieweeId)?.directReports?.set(participant.id, participant);
                return;
            }

            this.revieweesParticipantsList.get(revieweeId)?.others?.set(participant.id, participant);
        });

        participantsRemoved.forEach((participant) => {
            if (revieweesRelationships?.manager && participant.is(revieweesRelationships?.manager[0])) {
                const manager = this.revieweesParticipantsList.get(revieweeId)?.manager ?? [];
                this.revieweesParticipantsList.get(revieweeId)?.manager?.delete(participant.id);
                return;
            }

            if (revieweesRelationships?.self && participant.is(revieweesRelationships?.self[0])) {
                const self = this.revieweesParticipantsList.get(revieweeId)?.self ?? [];
                this.revieweesParticipantsList.get(revieweeId)?.self?.delete(participant.id);
                return;
            }

            if (revieweesRelationships?.peers?.filter((peer) => peer.id == participant.id).length != 0) {
                const peers = this.revieweesParticipantsList.get(revieweeId)?.peers ?? [];
                this.revieweesParticipantsList.get(revieweeId)?.peers?.delete(participant.id);
                return;
            }

            if (
                revieweesRelationships?.directReports?.filter((directReport) => directReport.id == participant.id)
                    .length != 0
            ) {
                const directReports = this.revieweesParticipantsList.get(revieweeId)?.directReports ?? [];
                this.revieweesParticipantsList.get(revieweeId)?.directReports?.delete(participant.id);
                return;
            }

            this.revieweesParticipantsList.get(revieweeId)?.others?.delete(participant.id);
        });

        this.rows = this.reviewees.map((reviewee: Employee) => this.mapEmployeesToRow(reviewee));
        this.updateSelection();
    }

    trackByRow(index: number, item) {
        return item.cells[0].employee.id;
    }

    checkboxChanged(participantTypeChanged: Partial<Selections>) {
        this.searchBar.searchTerm = '';

        if (!Object.values(participantTypeChanged)[0]) {
            this.checked(Object.keys(participantTypeChanged)[0]);
            return;
        }

        this.unChecked(Object.keys(participantTypeChanged)[0]);
    }

    private checked(participantType: string | null): void {
        this.rows = this.reviewees.map((reviewee: Employee) => {
            const revieweeRelationships =
                this.revieweesRelationships.get(reviewee.id)?.[participantType as keyof Reports] ?? [];

            const revieweeParticipantsList =
                this.revieweesParticipantsList.get(reviewee.id)?.[participantType as keyof Reports] ??
                new Map<number, Employee>();

            revieweeRelationships.forEach((participant) => {
                if (!revieweeParticipantsList.has(participant.id)) {
                    revieweeParticipantsList.set(participant.id, participant);
                }
            });

            const revieweeReport = this.revieweesParticipantsList.get(reviewee.id) ?? {
                manager: new Map<number, Employee>(),
                peers: new Map<number, Employee>(),
                directReports: new Map<number, Employee>(),
                self: new Map<number, Employee>(),
                others: new Map<number, Employee>(),
            };
            revieweeReport[participantType as keyof Reports] = revieweeParticipantsList;
            this.revieweesParticipantsList.set(reviewee.id, revieweeReport);

            return this.mapEmployeesToRow(reviewee);
        });
        this.updateSelection();
    }

    private unChecked(participantType: string | null): void {
        this.rows = this.reviewees.map((reviewee: Employee) => {
            const revieweeReport = this.revieweesParticipantsList.get(reviewee.id) ?? {
                manager: new Map<number, Employee>(),
                peers: new Map<number, Employee>(),
                directReports: new Map<number, Employee>(),
                self: new Map<number, Employee>(),
                others: new Map<number, Employee>(),
            };
            revieweeReport[participantType as keyof Reports] = new Map<number, Employee>();
            this.revieweesParticipantsList.set(reviewee.id, revieweeReport);
            return this.mapEmployeesToRow(reviewee);
        });
        this.updateSelection();
    }

    updateSelection(): void {
        this.computedRows = this.rows;
        this.participantList.emit(this.revieweesParticipantsList);
        this.appSimpleTableRows.emit(this.rows);
    }
}
